export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  /** Marks backend context-limit guidance so the limit survives a reload without re-parsing message text. */
  isContextLimitNotice?: boolean;
}

export interface ChatConnectedEvent {
  messageId: string;
  timestamp: string;
  user: {
    id: string;
    email: string;
  };
}

export interface ChatMessageEvent {
  messageId: string;
  delta: string;
  index: number;
  errorCode?: string;
  contextLimitReached?: boolean;
}

export interface ChatCompleteEvent {
  messageId: string;
  totalChunks: number;
}

export interface ChatErrorEvent {
  error: string;
  code?: string;
  contextLimitReached?: boolean;
}

export interface ChatError {
  message: string;
  code?: string;
}

export interface ConversationTokenUsageResponse {
  scope: 'conversation_history';
  contextWindow: number;
  /** Effective input budget after backend output reservation and safety margin. */
  effectiveInputBudget: number;
  /** Estimated full prompt tokens for the next request before adding the user's next prompt text. */
  estimatedPromptTokens: number;
  /** Authoritative remaining input budget percentage for display. */
  remainingPercentage: number;
}

export interface ConversationContextUsage extends ConversationTokenUsageResponse {
  usageRatio: number;
  usagePercentage: number;
  isNearLimit: boolean;
  isCritical: boolean;
}

export interface ChatPingEvent {
  messageId: string;
  timestamp: string;
}

export enum ChatEventType {
  Connected = 'connected',
  Message = 'message',
  Ping = 'ping',
  Complete = 'complete',
  Error = 'error',
}

export interface ChatEvent {
  type: ChatEventType;
  data: ChatConnectedEvent | ChatMessageEvent | ChatCompleteEvent | ChatErrorEvent | ChatPingEvent;
}

export interface ChatState {
  messages: ChatMessage[];
  isConnected: boolean;
  isLoading: boolean;
  error: ChatError | null;
  persistenceError: string | null;
  hasReachedContextLimit: boolean;
  contextUsage: ConversationContextUsage | null;
}

export interface DailyReport {
  url: string;
  date: string;
  generatedAt: string;
  message: string;
}

export interface DailyReportState {
  report: DailyReport | null;
  isLoading: boolean;
  error: boolean;
  isDismissed: boolean;
}
